import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Card, CardContent, Typography, IconButton, Tooltip, Chip, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Snackbar, Alert, CircularProgress } from "@mui/material";
import Headeer from "../../components/Headeer";
import { 
  DataGrid,
} from "@mui/x-data-grid";
import { tokens } from "../../theme";
import { useTheme } from "@emotion/react";
import CreateNewFile from "../../components/CreateNewFile";
import FileService from "../../services/doctorServices/FileService";
import { useNavigate } from "react-router-dom";
import FolderIcon from "@mui/icons-material/Folder";
import AddIcon from "@mui/icons-material/Add";
import BarChartIcon from "@mui/icons-material/BarChart";
import VisibilityIcon from "@mui/icons-material/Visibility";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import CopyFileDialog from "../../components/CopyFileDialog";
import CollapsibleListFilters, { ListFiltersExcelButton } from "../../components/CollapsibleListFilters";

export default function Files() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('filesPage');
  const fileService = useMemo(() => new FileService(), []);
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingFileName, setEditingFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [files, setFiles] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    fileName: "",
    doctorName: "",
    dateFrom: "",
    dateTo: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [exporting, setExporting] = useState(false);
  const [formTypeDialogOpen, setFormTypeDialogOpen] = useState(false);
  const [selectedFileForForm, setSelectedFileForForm] = useState(null);

  const buildCleanFilters = (source) => {
    const next = {};
    Object.entries(source).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        next[key] = String(value).trim().toLowerCase();
      }
    });
    return next;
  };

  const columns = [
    {
      field: "name",
      headerName: t('file'),
      flex: 2,
      minWidth: 250,
      valueGetter: (params) => params.row.name || "",
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[600]} 100%)`,
              fontWeight: 700,
            }}
          >
            <FolderIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Box sx={{ fontWeight: 600, color: colors.grey[100] }}>
              {params.row.name}
            </Box>
            <Box sx={{ fontSize: '0.75rem', color: colors.grey[400] }}>
              {t('fileId')} {params.row.id}
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: "created_by",
      headerName: t('createdBy'),
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => {
        // Backend'den gelen Creator objesinden doktor adını al
        const creator = params.row.Creator;
        if (creator && creator.name && creator.surname) {
          return `${creator.name} ${creator.surname}`;
        }
        // Fallback: Eğer Creator yoksa created_by ID'sini göster
        const createdById = params.row.created_by;
        return createdById ? `${t('doctorPrefix')}${createdById.substring(0, 8)}...` : t('unknown');
      },
      renderCell: (params) => {
        const creator = params.row.Creator;
        const createdById = params.row.created_by;
        
        // Debug için console.log (geliştirme sonrası kaldırılabilir)
        if (!creator && createdById) {
          console.log("Creator missing for file:", params.row.id, "created_by:", createdById, "Creator object:", creator, "Full row:", params.row);
        }
        
        // Creator bilgisi varsa adını göster
        let doctorName;
        if (creator && creator.name && creator.surname) {
          doctorName = `${creator.name} ${creator.surname}`;
        } else if (createdById) {
          // created_by zaten formatlanmış bir string olabilir, kontrol et
          const createdByIdStr = String(createdById);
          if (createdByIdStr.includes(t('doctorPrefix'))) {
            // Zaten formatlanmış, direkt kullan
            doctorName = createdByIdStr;
          } else {
            // UUID formatında, formatla
            doctorName = `${t('doctorPrefix')}${createdByIdStr.substring(0, 8)}...`;
          }
        } else {
          doctorName = t('unknown');
        }
        
        return (
          <Chip
            label={doctorName}
            size="small"
            sx={{
              backgroundColor: `${colors.blueAccent[700]}40`,
              color: colors.blueAccent[300],
              fontWeight: 600,
              border: `1px solid ${colors.blueAccent[500]}40`,
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: t('actions'),
      flex: 2,
      minWidth: 240,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('viewFile')}>
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                handleRowClick(params);
              }}
              sx={{
                backgroundColor: `${colors.blueAccent[700]}40`,
                border: `1px solid ${colors.blueAccent[500]}40`,
                '&:hover': {
                  backgroundColor: `${colors.blueAccent[600]}60`,
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <VisibilityIcon sx={{ fontSize: 18, color: colors.blueAccent[300] }} />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={t('addForm')}>
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                handleAddFormClick(params);
              }}
              sx={{
                background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
                color: 'white',
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: `0 4px 12px ${colors.greenAccent[500]}60`,
                },
                transition: 'all 0.3s ease',
              }}
            >
              <NoteAddIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={t('copyFile')}>
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                handleCopyClick(params.row);
              }}
              sx={{
                backgroundColor: `${colors.blueAccent[700]}40`,
                border: `1px solid ${colors.blueAccent[500]}40`,
                '&:hover': {
                  backgroundColor: `${colors.blueAccent[600]}60`,
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 18, color: colors.blueAccent[300] }} />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={t('editFileName')}>
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                handleEditClick(params.row);
              }}
              sx={{
                backgroundColor: `${colors.greenAccent[700]}40`,
                border: `1px solid ${colors.greenAccent[500]}40`,
                '&:hover': {
                  backgroundColor: `${colors.greenAccent[600]}60`,
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <EditIcon sx={{ fontSize: 18, color: colors.greenAccent[300] }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const handleClose = () => {
    setOpenDialog(false);
  };

  const fetchAllFiles = useCallback(async () => {
      try {
        setTableLoading(true);
        const response = await fileService.getAllFiles({
          withMeta: true,
          params: {
            name: appliedFilters.fileName,
            doctorName: appliedFilters.doctorName,
            dateFrom: appliedFilters.dateFrom,
            dateTo: appliedFilters.dateTo,
          },
          headers: {
            "x-bone-pageindex": paginationModel.page,
            "x-bone-limit": paginationModel.pageSize,
            "x-bone-sortby": "createdAt",
            "x-bone-sortorder": "DESC",
          },
        });
        const rows = Array.isArray(response?.data) ? response.data : [];
        setFiles(rows);
        setTotalCount(Number(response?.headers?.["x-bone-totalcount"]) || 0);
        // Debug: Creator bilgisini kontrol et
        if (rows.length > 0) {
          console.log("Files with Creator info:", rows.map(f => ({
            id: f.id,
            name: f.name,
            created_by: f.created_by,
            Creator: f.Creator
          })));
        }
      } catch (error) {
        console.log("Bir Sorun Oluştu", error);
        setFiles([]);
        setTotalCount(0);
      } finally {
        setTableLoading(false);
      }
    }, [fileService, appliedFilters, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    fetchAllFiles();
  }, [fetchAllFiles]);

  const handleRowClick = (params) => {
    navigate(`/files/${params.id}`);
  };

  const handleAddFormClick = (params) => {
    setSelectedFileForForm(params);
    setFormTypeDialogOpen(true);
  };

  const handleFormTypeSelection = (formType) => {
    if (selectedFileForForm) {
      const typeParam = formType === "for patients" ? "for%20patients" : "for%20me";
      navigate(`/Create-NewForm/${selectedFileForForm.id}?type=${typeParam}`);
    }
    setFormTypeDialogOpen(false);
    setSelectedFileForForm(null);
  };

  const handleFileCreated = (newFile) => {
    if (newFile) {
      setFiles((prev) => [newFile, ...prev]);
    } else {
      fetchAllFiles();
    }
  };

  const handleCopyClick = (file) => {
    setSelectedFile(file);
    setCopyDialogOpen(true);
  };

  const handleCopyFile = async (newFileName) => {
    try {
      const response = await fileService.copyFile(selectedFile.id, newFileName);
      
      // Listeyi yenile
      fetchAllFiles();
      
      return response;
    } catch (error) {
      console.error("Error copying file:", error);
      throw error;
    }
  };

  const capitalizeWords = (str) => {
    if (!str) return str;
    // Önce tüm string'i küçük harfe çevir, sonra kelime başlarını büyük yap
    return str.trim().toLowerCase().replace(/(^|\s)\S/g, (match) => match.toUpperCase());
  };

  const handleEditClick = (file) => {
    setSelectedFile(file);
    setEditingFileName(file.name || "");
    setEditDialogOpen(true);
  };

  const handleEditFile = async () => {
    if (!selectedFile || !editingFileName.trim()) {
      setSnackbar({
        open: true,
        message: t('fileNameEmpty'),
        severity: "error",
      });
      return;
    }

    const capitalizedName = capitalizeWords(editingFileName);
    
    if (capitalizedName === selectedFile.name) {
      setEditDialogOpen(false);
      return;
    }

    setLoading(true);
    try {
      await fileService.updateFile(selectedFile.id, { name: capitalizedName });
      setSnackbar({
        open: true,
        message: t('fileNameUpdated'),
        severity: "success",
      });
      setEditDialogOpen(false);
      fetchAllFiles();
    } catch (error) {
      console.error("Error updating file:", error);
      if (error.response?.status === 403) {
        return;
      }
      setSnackbar({
        open: true,
        message: error.response?.data?.message || t('errorUpdatingFileName'),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const activeFilterCount = Object.keys(appliedFilters).length;
  const handleExcelExport = async () => {
    try {
      setExporting(true);
      const response = await fileService.exportFilesExcel({
        name: appliedFilters.fileName,
        doctorName: appliedFilters.doctorName,
        dateFrom: appliedFilters.dateFrom,
        dateTo: appliedFilters.dateTo,
      });
      const contentDisposition = response.headers?.["content-disposition"] || "";
      const match = contentDisposition.match(/filename="?([^"]+)"?/i);
      const fileName = match?.[1] || `files-${Date.now()}.xlsx`;
      const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Files excel export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Headeer title={t('title')} subtitle={t('subtitle')} />
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <IconButton
            onClick={() => navigate('/files/statistics')}
            sx={{
              background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
              color: 'white',
              px: 3,
              py: 1.5,
              borderRadius: '12px',
              border: `2px solid ${colors.greenAccent[400]}40`,
              boxShadow: `0 4px 16px ${colors.blueAccent[500]}50`,
              transition: 'all 0.3s ease',
              '&:hover': {
                background: `linear-gradient(135deg, ${colors.blueAccent[400]} 0%, ${colors.greenAccent[400]} 100%)`,
                transform: 'translateY(-4px)',
                boxShadow: `0 6px 24px ${colors.greenAccent[500]}60`,
              }
            }}
          >
            <BarChartIcon sx={{ mr: 1 }} />
            <Box component="span" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
              {t('viewStatistics')}
            </Box>
          </IconButton>
          <IconButton
            onClick={() => setOpenDialog(true)}
            sx={{
              background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
              color: 'white',
              px: 3,
              py: 1.5,
              borderRadius: '12px',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${colors.greenAccent[500]}50`,
              }
            }}
          >
            <AddIcon sx={{ mr: 1 }} />
            <Box component="span" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
              {t('createFile')}
            </Box>
          </IconButton>
        </Box>
      </Box>

      <CollapsibleListFilters
        colors={colors}
        open={filtersOpen}
        onToggle={() => setFiltersOpen((prev) => !prev)}
        activeFilterCount={activeFilterCount}
        onApply={() => {
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
          setAppliedFilters(buildCleanFilters(filters));
        }}
        onClear={() => {
          setFilters({ fileName: "", doctorName: "", dateFrom: "", dateTo: "" });
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
          setAppliedFilters({});
        }}
        exportAction={
          <ListFiltersExcelButton
            colors={colors}
            onClick={handleExcelExport}
            disabled={exporting}
            loading={exporting}
          />
        }
      >
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2 }}>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 6" } }}>
            <TextField
              fullWidth
              size="small"
              label="File Name"
              value={filters.fileName}
              onChange={(e) => setFilters((prev) => ({ ...prev, fileName: e.target.value }))}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 6" } }}>
            <TextField
              fullWidth
              size="small"
              label="Doctor Name"
              value={filters.doctorName}
              onChange={(e) => setFilters((prev) => ({ ...prev, doctorName: e.target.value }))}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 6" } }}>
            <TextField
              fullWidth
              size="small"
              label="Date From"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 6" } }}>
            <TextField
              fullWidth
              size="small"
              label="Date To"
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>
      </CollapsibleListFilters>

      {/* DataGrid Card */}
      <Card
        sx={{
          background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
          borderRadius: '16px',
          border: `1px solid ${colors.grey[800]}30`,
          boxShadow: `0 4px 24px rgba(0, 0, 0, 0.1)`,
          overflow: 'hidden',
          mb: 4,
        }}
      >
        <Box
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: `${colors.blueAccent[700]}60`,
              borderBottom: `2px solid ${colors.blueAccent[500]}40`,
              borderRadius: 0,
              fontWeight: 700,
              fontSize: '0.95rem',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${colors.grey[800]}20`,
              fontSize: '0.9rem',
            },
            '& .MuiDataGrid-row': {
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: `${colors.blueAccent[800]}20`,
                cursor: 'pointer',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: `${colors.blueAccent[700]}40`,
              borderTop: `2px solid ${colors.blueAccent[500]}40`,
              borderRadius: 0,
            },
            '& .MuiTablePagination-root': {
              color: colors.grey[100],
            },
            '& .MuiDataGrid-toolbarContainer': {
              padding: 0,
            },
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <DataGrid
            rows={files}
            columns={columns}
            loading={tableLoading}
            autoHeight
            pagination
            paginationMode="server"
            rowCount={totalCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-row:nth-of-type(even)': {
                backgroundColor: `${colors.primary[500]}20`,
              },
            }}
            localeText={{
              noRowsLabel: (
                <Typography sx={{ py: 10, color: colors.grey[300] }}>
                  {t('noFilesMessage')}
                </Typography>
              ),
            }}
          />
        </Box>
      </Card>

      <CreateNewFile
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        handleClose={handleClose}
        onCreated={handleFileCreated}
      />

      <CopyFileDialog
        open={copyDialogOpen}
        onClose={() => setCopyDialogOpen(false)}
        originalFile={selectedFile}
        onCopy={handleCopyFile}
      />

      {/* Edit File Name Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !loading && setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%)`,
            borderRadius: '16px',
            border: `1px solid ${colors.blueAccent[500]}40`,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: colors.grey[100],
            fontWeight: 700,
            fontSize: '1.5rem',
            borderBottom: `1px solid ${colors.blueAccent[500]}40`,
            pb: 2,
          }}
        >
          {t('editFileName')}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label={t('fileName')}
            variant="filled"
            value={editingFileName}
            onChange={(e) => setEditingFileName(e.target.value)}
            disabled={loading}
            sx={{
              '& .MuiFilledInput-root': {
                backgroundColor: colors.primary[400],
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: colors.primary[500],
                },
                '&.Mui-focused': {
                  backgroundColor: colors.primary[500],
                },
              },
              '& .MuiInputLabel-root': {
                color: colors.grey[300],
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: colors.blueAccent[300],
              },
              '& .MuiFilledInput-input': {
                color: colors.grey[100],
              },
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !loading) {
                handleEditFile();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            disabled={loading}
            sx={{
              color: colors.grey[300],
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: `${colors.grey[700]}40`,
              },
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleEditFile}
            disabled={loading || !editingFileName.trim()}
            variant="contained"
            sx={{
              background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px ${colors.greenAccent[500]}60`,
              },
              '&:disabled': {
                opacity: 0.5,
              },
              transition: 'all 0.3s ease',
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: 'white' }} />
                <span>{t('saving')}</span>
              </Box>
            ) : (
              t('saveChanges')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Form Type Selection Dialog */}
      <Dialog
        open={formTypeDialogOpen}
        onClose={() => {
          setFormTypeDialogOpen(false);
          setSelectedFileForForm(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%)`,
            borderRadius: '16px',
            border: `1px solid ${colors.blueAccent[500]}40`,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: colors.grey[100],
            fontWeight: 700,
            fontSize: '1.5rem',
            borderBottom: `1px solid ${colors.blueAccent[500]}40`,
            pb: 2,
          }}
        >
          {t('selectFormType')}
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Typography
            variant="body1"
            sx={{
              color: colors.grey[300],
              mb: 3,
            }}
          >
            {t('chooseFormType')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handleFormTypeSelection("for patients")}
              sx={{
                p: 3,
                border: `2px solid ${colors.greenAccent[500]}`,
                color: colors.grey[100],
                backgroundColor: `${colors.greenAccent[700]}20`,
                borderRadius: '12px',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                '&:hover': {
                  border: `2px solid ${colors.greenAccent[400]}`,
                  backgroundColor: `${colors.greenAccent[700]}40`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${colors.greenAccent[500]}40`,
                },
                transition: 'all 0.3s ease',
              }}
            >
              <Box sx={{ textAlign: 'left', width: '100%' }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                  {t('forPatients')}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                  {t('forPatientsDescription')}
                </Typography>
              </Box>
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handleFormTypeSelection("for me")}
              sx={{
                p: 3,
                border: `2px solid ${colors.blueAccent[500]}`,
                color: colors.grey[100],
                backgroundColor: `${colors.blueAccent[700]}20`,
                borderRadius: '12px',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                '&:hover': {
                  border: `2px solid ${colors.blueAccent[400]}`,
                  backgroundColor: `${colors.blueAccent[700]}40`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${colors.blueAccent[500]}40`,
                },
                transition: 'all 0.3s ease',
              }}
            >
              <Box sx={{ textAlign: 'left', width: '100%' }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                  {t('forMe')}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                  {t('forMeDescription')}
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => {
              setFormTypeDialogOpen(false);
              setSelectedFileForForm(null);
            }}
            sx={{
              color: colors.grey[300],
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: `${colors.grey[700]}40`,
              },
            }}
          >
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            fontSize: "1rem",
            "& .MuiAlert-message": {
              fontWeight: 500,
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
